import * as React from 'react';
import * as firebase from "firebase/app";
import { Provider, connect } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import createStore from './createStore';
import { ThemeProvider } from '@material-ui/core/styles';
import { createAppTheme } from './theme';
import CordovaContext from './cordovaContext';
import { Router } from 'react-router-dom';
import { createHashHistory, createBrowserHistory, History } from 'history';
import PublicRouter from '@router/public.router';
import * as CommonActions from '@services/common/common.action'; // Ensure this path is correct
import * as FileActions from '@services/file/file.actions';
import {closeArticle} from '@services/article/article.actions';
import WebSocketClient from '@clients/web-socket.client';
import { markAppOpened } from '@helpers/appLifecycle';
import { applyDocumentLanguage } from '@helpers/appLanguage';
import { useSystemDarkMode } from '@helpers/colorScheme';
import { isUserInCall } from '@services/calls/calls.utils';
import { Store } from 'redux';
import { Persistor } from 'redux-persist';

export let store: Store<any>;
export let persistor: Persistor;

// The first launch on the new native origin must restore localStorage before
// persistStore starts reading it. All other launches initialize synchronously.
export function initializeAppStore(): void {
  if (!store) {
    const initialized = createStore();
    store = initialized.store;
    persistor = initialized.persistor;
  }
}

const fbConfig = {
  apiKey: 'AIzaSyA7zkpHWLXGJ7Cs5arR4gG-XCseR8oaPJM',
  messagingSenderId: '1086088344303',
  projectId: 'varannanvecka',
};

interface IProps {
  cordovaApp?: boolean;
}

if (!firebase.apps.length) {
  firebase.initializeApp(fbConfig);
}

const lightTheme = createAppTheme('light');
const darkTheme = createAppTheme('dark');
const HIDDEN_SOCKET_RECONNECT_THRESHOLD_MS = 3 * 60 * 1000;
const RESUME_SOCKET_HEALTH_TIMEOUT_MS = 3000;
const FILE_PICKER_LOADING_SUPPRESSION_MS = 5 * 60 * 1000;

interface IThemedRootProps {
  darkMode?: boolean;
  language?: string;
  children: React.ReactNode;
}

const ThemedRootImpl: React.FC<IThemedRootProps> = ({ darkMode, language, children }) => {
  // The dark mode setting is always dark or always light; without one the app follows the phone or browser.
  const systemDarkMode = useSystemDarkMode();
  const dark = darkMode === undefined ? systemDarkMode : darkMode;

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    applyDocumentLanguage(language);
  }, [dark, language]);

  return (
    <ThemeProvider theme={dark ? darkTheme : lightTheme}>
      {children}
    </ThemeProvider>
  );
};

const ThemedRoot = connect((state: any) => ({
  darkMode: typeof state?.user?.darkMode === 'boolean' ? state.user.darkMode : undefined,
  language: state?.user?.language,
}))(ThemedRootImpl);

export default class App extends React.Component<IProps> {
  private history: History;
  private removeBackHandler?: () => void;
  private removeFileInputLoadingSuppressionHandler?: () => void;
  private resumeReconnectTimer?: number;
  private hiddenSocketReconnectTimer?: number;
  private hiddenSocketReconnectTimerId = 0;
  private resumeHealthCheckId = 0;
  private lastPausedAt = 0;

  constructor(props: IProps) {
    super(props);
    const isCordova = !!props.cordovaApp;
    this.history = isCordova ? createHashHistory() : createBrowserHistory();
  }

  public componentDidMount() {
    const isCordova = !!this.props.cordovaApp;

    const suppressLoadingForFilePicker = (event: Event) => {
      if (this.eventCanOpenFilePicker(event)) {
        const suppressUntil = Date.now() + FILE_PICKER_LOADING_SUPPRESSION_MS;
        store.dispatch(FileActions.suppressLoadingScreenForFilePickerUntil(
          suppressUntil
        ));
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', suppressLoadingForFilePicker, true);
      document.addEventListener('touchstart', suppressLoadingForFilePicker, true);
      document.addEventListener('click', suppressLoadingForFilePicker, true);

      this.removeFileInputLoadingSuppressionHandler = () => {
        document.removeEventListener('mousedown', suppressLoadingForFilePicker, true);
        document.removeEventListener('touchstart', suppressLoadingForFilePicker, true);
        document.removeEventListener('click', suppressLoadingForFilePicker, true);
      };
    }

    const triggerBootstrap = () => {
      store.dispatch(CommonActions.bootstrap({
        meta: { history: this.history }
      }));
    };

    const clearHiddenSocketReconnectTimer = () => {
      this.hiddenSocketReconnectTimerId++;
      if (this.hiddenSocketReconnectTimer !== undefined) {
        window.clearTimeout(this.hiddenSocketReconnectTimer);
        this.hiddenSocketReconnectTimer = undefined;
      }
    };

    const markSocketStale = () => {
      store.dispatch(CommonActions.websocketDisconnected());

      const ws = WebSocketClient.getInstance();
      try {
        ws.close(false);
      } catch (error) {
        // The socket may not exist yet on cold start.
      }
    };

    const hasOpenAuthenticatedSocket = () => {
      try {
        const ws = WebSocketClient.getInstance();
        const socket = ws.getSocket();
        return !!(ws.hasConnection() && socket && socket.readyState === WebSocket.OPEN);
      } catch (error) {
        return false;
      }
    };

    const hasActiveCall = () => {
      try {
        const state: any = store.getState();
        const userId = state && state.user && state.user.session && state.user.session.data
          ? state.user.session.data.id
          : undefined;
        const calls = state && state.calls && state.calls.calls ? state.calls.calls : [];
        if (userId === undefined || !Array.isArray(calls)) {
          return false;
        }

        // Shared with the call UI so the two cannot drift apart on what counts as being in
        // a call - this decides whether the socket may be dropped while the app is hidden.
        return calls.some((call: any) => !!call && isUserInCall(call, userId));
      } catch (error) {
        return false;
      }
    };

    const markSocketPaused = () => {
      if (!hasActiveCall()) {
        this.lastPausedAt = 0;
        this.resumeHealthCheckId++;
        clearHiddenSocketReconnectTimer();
        return;
      }

      this.lastPausedAt = Date.now();
      this.resumeHealthCheckId++;
      clearHiddenSocketReconnectTimer();
      const pauseStartedAt = this.lastPausedAt;
      const timerId = ++this.hiddenSocketReconnectTimerId;

      // Android can fire Cordova pause when opening native pickers. Keep the
      // socket alive for short pauses and let resume verify it before reconnecting.
      this.hiddenSocketReconnectTimer = window.setTimeout(() => {
        this.hiddenSocketReconnectTimer = undefined;
        if (timerId === this.hiddenSocketReconnectTimerId && this.lastPausedAt === pauseStartedAt) {
          markSocketStale();
        }
      }, HIDDEN_SOCKET_RECONNECT_THRESHOLD_MS);
    };

    const markSocketHidden = () => {
      if (isCordova && !hasActiveCall()) {
        this.lastPausedAt = 0;
        this.resumeHealthCheckId++;
        clearHiddenSocketReconnectTimer();
        return;
      }

      this.lastPausedAt = Date.now();
      this.resumeHealthCheckId++;
      clearHiddenSocketReconnectTimer();
      const hiddenStartedAt = this.lastPausedAt;
      const timerId = ++this.hiddenSocketReconnectTimerId;

      this.hiddenSocketReconnectTimer = window.setTimeout(() => {
        this.hiddenSocketReconnectTimer = undefined;
        if (timerId === this.hiddenSocketReconnectTimerId
          && this.lastPausedAt === hiddenStartedAt
          && typeof document !== 'undefined'
          && document.hidden) {
          markSocketStale();
        }
      }, HIDDEN_SOCKET_RECONNECT_THRESHOLD_MS);
    };

    const scheduleBootstrap = (delay: number = 250) => {
      if (this.resumeReconnectTimer !== undefined) {
        window.clearTimeout(this.resumeReconnectTimer);
      }

      this.resumeReconnectTimer = window.setTimeout(() => {
        this.resumeReconnectTimer = undefined;
        triggerBootstrap();
      }, delay);
    };

    const onResume = () => {
      markAppOpened();

      const backgroundMs = this.lastPausedAt > 0 ? Date.now() - this.lastPausedAt : 0;
      const healthCheckId = ++this.resumeHealthCheckId;
      this.lastPausedAt = 0;
      clearHiddenSocketReconnectTimer();

      if (backgroundMs <= 0) {
        if (!hasOpenAuthenticatedSocket()) {
          scheduleBootstrap(0);
        }
        return;
      }

      if (backgroundMs >= HIDDEN_SOCKET_RECONNECT_THRESHOLD_MS) {
        markSocketStale();
        scheduleBootstrap(250);
        return;
      }

      if (isCordova) {
        if (!hasOpenAuthenticatedSocket()) {
          scheduleBootstrap(0);
        }
        return;
      }

      console.log("Testing websocket connection is still active.");

      WebSocketClient.getInstance().checkAlive(RESUME_SOCKET_HEALTH_TIMEOUT_MS)
        .then((alive) => {
          if (healthCheckId !== this.resumeHealthCheckId) {
            return;
          }

          if (!alive) {
            console.log("Websocket connection check failed, reconnecting.");
            markSocketStale();
            scheduleBootstrap(0);
          } else {
            console.log("Websocket connection check succeeded.");
          }
        });
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        markSocketHidden();
      } else {
        onResume();
      }
    };

    if (isCordova && typeof document !== 'undefined') {
      const onReady = () => {
        // 1. Trigger bootstrap on initial launch
        triggerBootstrap();

        // 2. Handle background -> foreground resume (Cordova specific)
        document.addEventListener('pause', markSocketPaused, false);
        document.addEventListener('resume', onResume, false);
        document.addEventListener('visibilitychange', onVisibilityChange, false);

        const onBackButton = (ev: Event) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (store.getState().articleViewer?.content) store.dispatch(closeArticle());
        };
        document.addEventListener('backbutton', onBackButton, false);

        this.removeBackHandler = () => {
          document.removeEventListener('backbutton', onBackButton, false);
          document.removeEventListener('pause', markSocketPaused, false);
          document.removeEventListener('resume', onResume);
          document.removeEventListener('visibilitychange', onVisibilityChange, false);
        };
      };

      if ((window as any).cordova) {
        document.addEventListener('deviceready', onReady, { once: true });
      } else {
        onReady();
      }
    } else {
      // Standard Web behavior
      triggerBootstrap();
      window.addEventListener('online', onResume);
      window.addEventListener('pageshow', onResume);
      document.addEventListener('visibilitychange', onVisibilityChange, false);
      this.removeBackHandler = () => {
        window.removeEventListener('online', onResume);
        window.removeEventListener('pageshow', onResume);
        document.removeEventListener('visibilitychange', onVisibilityChange, false);
      };
    }
  }

  public componentWillUnmount() {
    if (this.resumeReconnectTimer !== undefined) {
      window.clearTimeout(this.resumeReconnectTimer);
      this.resumeReconnectTimer = undefined;
    }
    if (this.hiddenSocketReconnectTimer !== undefined) {
      this.hiddenSocketReconnectTimerId++;
      window.clearTimeout(this.hiddenSocketReconnectTimer);
      this.hiddenSocketReconnectTimer = undefined;
    }
    this.resumeHealthCheckId++;
    if (this.removeBackHandler) this.removeBackHandler();
    if (this.removeFileInputLoadingSuppressionHandler) this.removeFileInputLoadingSuppressionHandler();
  }

  public render() {
    const isCordova = !!this.props.cordovaApp;

    return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemedRoot>
            <CordovaContext.Provider value={isCordova}>
              <Router history={this.history}>
                <PublicRouter />
              </Router>
            </CordovaContext.Provider>
          </ThemedRoot>
        </PersistGate>
      </Provider>
    );
  }

  private eventCanOpenFilePicker(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof Element)) {
      return false;
    }

    const input = target.closest('input');
    if (input instanceof HTMLInputElement && input.type === 'file') {
      return true;
    }

    const label = target.closest('label');
    if (!label) {
      return false;
    }

    const nestedFileInput = label.querySelector('input[type="file"]');
    if (nestedFileInput) {
      return true;
    }

    const htmlFor = (label as HTMLLabelElement).htmlFor;
    if (!htmlFor) {
      return false;
    }

    const labelledControl = document.getElementById(htmlFor);
    return labelledControl instanceof HTMLInputElement && labelledControl.type === 'file';
  }
}



// WEBPACK FOOTER //
// ./src/App.tsx